import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import apiService from '../services/apiService';
import { SectionHeader } from '../components/UI';
import { Edit2, Save, X, MapPin, Mail, Phone, Lock, Briefcase, Shield, Link, Linkedin } from 'lucide-react';

const SECURITY_QUESTIONS = [
  'What is your mother\'s maiden name?',
  'What was the name of your first pet?',
  'What city were you born in?',
  'What is your favorite book?',
  'What was the name of your first school?',
  'What is your favorite movie?',
  'What was your first car\'s make and model?',
  'In what city or town did your mother and father meet?',
];

export default function EXPERTProfileEdit() {
  const { user, updateUser, refreshExperts, toast } = useApp();
  const [saving, setSaving] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);

  useEffect(() => {
    apiService.getLanguages()
      .then(data => setAvailableLanguages((data.data || data).filter(l => l.enabled)))
      .catch(() => {});
  }, []);
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState(user?.profileImage || null);
  const [imagePreview, setImagePreview] = useState(user?.profileImage || null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showSecurityForm, setShowSecurityForm] = useState(false);
  const [securityData, setSecurityData] = useState({
    securityQuestion: user?.securityQuestion || '',
    securityAnswer: '',
  });
  const [securityLoading, setSecurityLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    email: user?.email || '',
    phone: user?.phone || user?.mobile || '',
    location: user?.city || '',
    category: user?.catName || 'Language',
    bio: user?.bio || '',
    experience: user?.experience || '',
    credentials: user?.credentials || '',
    languages: user?.languages || '',
    introLink: user?.introLink || '',
    linkedinUrl: user?.linkedinUrl || '',
    joinDate: user?.joinDate || new Date().toISOString().split('T')[0],
    totalSessions: user?.totalSessions || 0,
    rating: user?.rating || 4.8,
  });

  const selectedLanguages = formData.languages
    ? formData.languages.split(',').map(l => l.trim()).filter(Boolean)
    : [];

  const toggleLanguage = (lang) => {
    const current = selectedLanguages;
    const updated = current.includes(lang)
      ? current.filter(l => l !== lang)
      : [...current, lang];
    setFormData(prev => ({ ...prev, languages: updated.join(', ') }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast('Image size must be less than 5MB', 'error');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast('Please upload an image file', 'error');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast('Name and email are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const [firstName, ...rest] = formData.name.trim().split(' ');
      const lastName = rest.join(' ') || '';
      await apiService.updateProfile({
        firstName,
        lastName,
        bio: formData.bio,
        city: formData.location,
        mobile: formData.phone,
        profileImage: profileImage || null,
        credentials: formData.credentials || null,
        experience: String(formData.experience) || null,
        languages: formData.languages || null,
        introLink: formData.introLink || null,
        linkedinUrl: formData.linkedinUrl || null,
      });
      updateUser({
        ...formData,
        profileImage,
        name: formData.name,
        city: formData.location,
        languages: formData.languages,
        introLink: formData.introLink,
        linkedinUrl: formData.linkedinUrl,
      });
      await refreshExperts();
      toast('Profile updated successfully ✓', 'success');
      setIsEditing(false);
    } catch (err) {
      toast(err.message || 'Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      email: user?.email || '',
      phone: user?.phone || user?.mobile || '',
      location: user?.city || '',
      category: user?.catName || 'Language',
      bio: user?.bio || '',
      experience: user?.experience || '',
      credentials: user?.credentials || '',
      languages: user?.languages || '',
      introLink: user?.introLink || '',
      linkedinUrl: user?.linkedinUrl || '',
      joinDate: user?.joinDate || new Date().toISOString().split('T')[0],
      totalSessions: user?.totalSessions || 0,
      rating: user?.rating || 4.8,
    });
    setImagePreview(user?.profileImage || null);
    setProfileImage(user?.profileImage || null);
    setIsEditing(false);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast('All password fields are required', 'error');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast('New passwords do not match', 'error');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast('Password must be at least 6 characters', 'error');
      return;
    }
    toast('Password changed successfully ✓', 'success');
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordForm(false);
  };

  const handlePasswordCancel = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordForm(false);
  };

  const handleSecurityChange = (e) => {
    const { name, value } = e.target;
    setSecurityData(prev => ({ ...prev, [name]: value }));
  };

  const handleSecuritySubmit = async () => {
    if (!securityData.securityQuestion || !securityData.securityAnswer) {
      toast('Please select a question and provide an answer', 'error');
      return;
    }
    if (securityData.securityAnswer.trim().length < 2) {
      toast('Answer must be at least 2 characters', 'error');
      return;
    }
    setSecurityLoading(true);
    try {
      const res = await apiService.updateSecurityQuestion(
        user.email,
        securityData.securityQuestion,
        securityData.securityAnswer.trim().toLowerCase()
      );
      if (res.success) {
        updateUser({ securityQuestion: securityData.securityQuestion });
        toast('Security question updated successfully ✓', 'success');
        setShowSecurityForm(false);
      } else {
        toast(res.message || 'Failed to update security question', 'error');
      }
    } catch (err) {
      toast(err.message || 'Failed to update security question', 'error');
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleSecurityCancel = () => {
    setSecurityData({ securityQuestion: user?.securityQuestion || '', securityAnswer: '' });
    setShowSecurityForm(false);
  };

  return (
    <div className="animate-fadeUp">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <SectionHeader title="My Profile" subtitle="Manage your expert profile, credentials, and experience." />
        {!isEditing ? (
          <button onClick={() => setIsEditing(true)} className="btn btn-primary" style={{ gap: 6 }}>
            <Edit2 size={16} /> Edit Profile
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ gap: 6 }}>
              <Save size={16} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={handleCancel} className="btn btn-secondary" style={{ gap: 6 }}>
              <X size={16} /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Profile Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 28 }}>
        {/* Avatar Card */}
        <div className="card card-body">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative' }}>
              <div style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: imagePreview ? `url(${imagePreview})` : `linear-gradient(135deg, var(--saffron) 0%, var(--teal) 100%)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 48,
                fontWeight: 700,
                border: '3px solid var(--surface)',
                boxShadow: 'var(--shadow-md)'
              }}>
                {!imagePreview && formData.name.charAt(0).toUpperCase()}
              </div>
              {isEditing && (
                <label style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  background: 'var(--saffron)',
                  color: 'white',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '3px solid var(--surface)',
                  boxShadow: 'var(--shadow-md)'
                }}>
                  <Edit2 size={16} />
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{formData.name}</h2>
              <p style={{ fontSize: 13, color: 'var(--ink3)' }}>{formData.category} Expert</p>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="card card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--ink4)', marginBottom: 4 }}>Total Sessions</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--saffron)' }}>{formData.totalSessions}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--ink4)', marginBottom: 4 }}>Rating</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--teal)' }}>{formData.rating}★</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--ink4)', marginBottom: 4 }}>Experience</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{formData.experience} yrs</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--ink4)', marginBottom: 4 }}>Member Since</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{new Date(formData.joinDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short' })}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="card card-body">
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Personal Information</h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
          {/* Name */}
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
              />
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14 }}>
                {formData.name}
              </div>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label">Email Address *</label>
            {isEditing ? (
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                disabled
                style={{ background: 'var(--bg2)', cursor: 'not-allowed', opacity: 0.6 }}
                placeholder="your@email.com"
              />
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Mail size={14} style={{ color: 'var(--ink3)' }} />
                {formData.email}
              </div>
            )}
          </div>

          {/* Phone */}
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            {isEditing ? (
              <input
                type="tel"
                name="phone"
                className="form-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 XXXXX XXXXX"
              />
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Phone size={14} style={{ color: 'var(--ink3)' }} />
                {formData.phone || 'Not provided'}
              </div>
            )}
          </div>

          {/* Location */}
          <div className="form-group">
            <label className="form-label">Location</label>
            {isEditing ? (
              <input
                type="text"
                name="location"
                className="form-input"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, Country"
              />
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapPin size={14} style={{ color: 'var(--ink3)' }} />
                {formData.location || 'Not provided'}
              </div>
            )}
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Expertise Category</label>
            {isEditing ? (
              <select
                name="category"
                className="form-select"
                value={formData.category}
                onChange={handleChange}
              >
                <option>Language</option>
                <option>Music</option>
                <option>Yoga & Fitness</option>
                <option>IT & Coding</option>
                <option>Academic</option>
                <option>Counseling</option>
                <option>Art & Design</option>
                <option>Business</option>
              </select>
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Briefcase size={14} style={{ color: 'var(--ink3)' }} />
                {formData.category}
              </div>
            )}
          </div>

          {/* Experience */}
          <div className="form-group">
            <label className="form-label">Years of Experience</label>
            {isEditing ? (
              <input
                type="number"
                name="experience"
                className="form-input"
                value={formData.experience}
                onChange={handleChange}
                placeholder="e.g. 5"
                min="0"
              />
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14 }}>
                {formData.experience ? `${formData.experience} years` : 'Not provided'}
              </div>
            )}
          </div>
        </div>

        {/* Languages */}
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label className="form-label">Languages You Teach / Speak</label>
          {isEditing ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8 }}>
              {availableLanguages.map(l => {
                const lang = l.name;
                const selected = selectedLanguages.includes(lang);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => toggleLanguage(lang)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      border: `1.5px solid ${selected ? 'var(--teal)' : 'var(--border2)'}`,
                      background: selected ? '#E3F4F2' : 'var(--surface)',
                      color: selected ? 'var(--teal)' : 'var(--ink3)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14 }}>
              {selectedLanguages.length > 0
                ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {selectedLanguages.map(l => (
                      <span key={l} className="badge badge-teal" style={{ fontSize: 12, padding: '3px 10px' }}>{l}</span>
                    ))}
                  </div>
                : 'Not provided'}
            </div>
          )}
        </div>

        {/* Bio */}
        <div className="form-group">
          <label className="form-label">About You / Bio</label>
          {isEditing ? (
            <textarea
              name="bio"
              className="form-textarea"
              value={formData.bio}
              onChange={handleChange}
              placeholder="Tell learners about your background, teaching style, certifications, experience…"
              rows={5}
            />
          ) : (
            <div style={{ padding: '12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14, lineHeight: 1.6, minHeight: 100 }}>
              {formData.bio || 'No bio provided'}
            </div>
          )}
        </div>

        {/* Credentials */}
        <div className="form-group">
          <label className="form-label">Credentials & Certifications</label>
          {isEditing ? (
            <textarea
              name="credentials"
              className="form-textarea"
              value={formData.credentials}
              onChange={handleChange}
              placeholder="e.g. B.A. in English, IELTS Certified, 200+ Hours Yoga Training…"
              rows={4}
            />
          ) : (
            <div style={{ padding: '12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14, lineHeight: 1.6, minHeight: 80 }}>
              {formData.credentials || 'No credentials provided'}
            </div>
          )}
        </div>

        {/* Links Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {/* Intro Video Link */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Link size={13} style={{ color: 'var(--teal)' }} /> Intro Video Link
            </label>
            {isEditing ? (
              <input
                type="url"
                name="introLink"
                className="form-input"
                value={formData.introLink}
                onChange={handleChange}
                placeholder="https://youtube.com/watch?v=..."
              />
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14 }}>
                {formData.introLink
                  ? <a href={formData.introLink} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', wordBreak: 'break-all' }}>{formData.introLink}</a>
                  : 'Not provided'}
              </div>
            )}
          </div>

          {/* LinkedIn URL */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Linkedin size={13} style={{ color: '#0A66C2' }} /> LinkedIn Profile
            </label>
            {isEditing ? (
              <input
                type="url"
                name="linkedinUrl"
                className="form-input"
                value={formData.linkedinUrl}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/your-profile"
              />
            ) : (
              <div style={{ padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14 }}>
                {formData.linkedinUrl
                  ? <a href={formData.linkedinUrl} target="_blank" rel="noreferrer" style={{ color: '#0A66C2', wordBreak: 'break-all' }}>{formData.linkedinUrl}</a>
                  : 'Not provided'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="card card-body" style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>Change Password</h3>
          {!showPasswordForm && (
            <button onClick={() => setShowPasswordForm(true)} className="btn btn-secondary btn-sm" style={{ gap: 6 }}>
              <Lock size={14} /> Change Password
            </button>
          )}
        </div>

        {showPasswordForm ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {/* Current Password */}
            <div className="form-group">
              <label className="form-label">Current Password *</label>
              <input
                type="password"
                name="currentPassword"
                className="form-input"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                placeholder="Enter your current password"
              />
            </div>

            {/* New Password */}
            <div className="form-group">
              <label className="form-label">New Password *</label>
              <input
                type="password"
                name="newPassword"
                className="form-input"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label className="form-label">Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                className="form-input"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Confirm your new password"
              />
            </div>
          </div>
        ) : (
          <p style={{ fontSize: 14, color: 'var(--ink3)' }}>Secure your account by changing your password regularly.</p>
        )}

        {showPasswordForm && (
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button onClick={handlePasswordSubmit} className="btn btn-primary" style={{ gap: 6 }}>
              <Save size={16} /> Update Password
            </button>
            <button onClick={handlePasswordCancel} className="btn btn-secondary" style={{ gap: 6 }}>
              <X size={16} /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Security Questions Section */}
      <div className="card card-body" style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>Security Question</h3>
          {!showSecurityForm && (
            <button onClick={() => setShowSecurityForm(true)} className="btn btn-secondary btn-sm" style={{ gap: 6 }}>
              <Shield size={14} /> Update Question
            </button>
          )}
        </div>

        {showSecurityForm ? (
          <div>
            {/* Security Question Dropdown */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Security Question *</label>
              <select
                name="securityQuestion"
                className="form-select"
                value={securityData.securityQuestion}
                onChange={handleSecurityChange}
              >
                <option value="">— Choose a security question —</option>
                {SECURITY_QUESTIONS.map((q, i) => (
                  <option key={i} value={q}>{q}</option>
                ))}
              </select>
            </div>

            {/* Security Answer Input */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Your Answer *</label>
              <input
                type="text"
                name="securityAnswer"
                className="form-input"
                value={securityData.securityAnswer}
                onChange={handleSecurityChange}
                placeholder="Your answer (case-insensitive)"
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSecuritySubmit} disabled={securityLoading} className="btn btn-primary" style={{ gap: 6 }}>
                <Save size={16} /> {securityLoading ? 'Updating...' : 'Update Question'}
              </button>
              <button onClick={handleSecurityCancel} className="btn btn-secondary" style={{ gap: 6 }}>
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 12 }}>This question helps you recover your account if you forget your password.</p>
            {securityData.securityQuestion && (
              <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--ink4)', marginBottom: 4 }}>Current Question:</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink2)' }}>{securityData.securityQuestion}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
